"use client"

import React, {ReactNode} from 'react';
import {type Control, type FieldPath, useFieldArray, useForm} from "react-hook-form"
import {valibotResolver} from "@hookform/resolvers/valibot"
import type {InferInput} from "valibot";
import {ClearanceWaypointsSchema} from "@root/server/routes/web/order_wizard/[order_edit_id]/clearance/clearance.shared.ts"
import {Button} from "@root/server/client/esweb/components/ui/button.tsx"
import {Trash2} from "lucide-react"
import {RhfNativeSelect} from "@root/server/client/esweb/components/ui/rhf/RhfSelect.tsx";
import {RhfAddressAutocomplete} from "@root/server/client/esweb/components/ui/rhf/RhfAddressAutocomplete.tsx";
import {ReactWrapperController} from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import {Context} from "@hotwired/stimulus";
import {ContinueSubmitButton} from "@root/server/client/esweb/src/pages/web/shared/continue-submit-button.tsx";
import {FormServerErrors} from "@root/server/client/esweb/src/pages/web/shared/form-server-errors.tsx";
import {useFormSubmission} from "@root/server/client/esweb/src/lib/hooks/useFormSubmission.ts";
import Debug from 'debug';


export const name = 'pwm--clearance_waypoints_form';
const debug = Debug('app:' + name);

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <ClearanceWaypointsFormStimulus {...props}/>)
    }
}

const FLOOR_OPTIONS = [
    {value: 0, label: "Přízemí"},
    {value: 1, label: "1. patro"},
    {value: 2, label: "2. patro"},
    {value: 3, label: "3. patro"},
    {value: 4, label: "4. patro"},
    {value: 5, label: "5. patro"},
]


type ClearanceWaypointsFormValues = InferInput<typeof ClearanceWaypointsSchema>;

type StopoverAddress = ClearanceWaypointsFormValues['stopoverAddresses'][number];

interface ClearanceWaypointsFormProps {
    initialData?: Partial<ClearanceWaypointsFormValues>;
    submitUrl: string;
}

interface AddressSectionProps {
    control: Control<ClearanceWaypointsFormValues>
    namePrefix: FieldPath<ClearanceWaypointsFormValues>
}

const AddressSection: React.FC<AddressSectionProps> = ({
    control,
    namePrefix,
}) => {
    return (
        <div className="flex flex-wrap gap-2">
            <RhfAddressAutocomplete
                control={control}
                name={`${namePrefix}.address` as FieldPath<ClearanceWaypointsFormValues>}
                placeholder="Např. Praha 5, Smíchov"
                className="w-auto grow min-w-[300px]"
            />

            <RhfNativeSelect
                className="max-w-28"
                control={control}
                name={`${namePrefix}.floor` as FieldPath<ClearanceWaypointsFormValues>}
                placeholder="Patro"
                options={FLOOR_OPTIONS}
            />

            <RhfNativeSelect
                className="max-w-32"
                control={control}
                name={`${namePrefix}.elevator` as FieldPath<ClearanceWaypointsFormValues>}
                placeholder="Výtah"
                options={[{label: 'Výtah je', value: true}, {label: 'Výtah není', value: false}]}
            />

            {/*<RhfCheckbox*/}
            {/*    {...{control}}*/}
            {/*    name={`${namePrefix}.elevator` as FieldPath<ClearanceWaypointsFormValues>}*/}
            {/*    label="Je k dispozici výtah?"*/}
            {/*/>*/}
        </div>
    )
}

function AddressContainer(props: {children: ReactNode, title: string, onRemove?: () => void, index: number, color: string}) {
    const {
        children,
        title,
        index,
        color,
        onRemove
    } = props;

    const colors = {
        orange: {badge: 'bg-orange-500', wrapper: 'border-orange-200- bg-orange-50-'},
        blue:   {badge: 'bg-blue-500'  , wrapper: 'border-blue-200- bg-blue-50-'},
        green:  {badge: 'bg-green-500' , wrapper: 'border-green-200- bg-green-50-'}
    }

    const removeButton = onRemove && <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onRemove()}
        className="text-destructive hover:text-destructive"
    >
        <Trash2 className="h-4 w-4" />
    </Button>

    return <div className={`rounded-xl border p-4 max-sm:-mx-4 space-y-2 ${colors[color].wrapper}`}>
        <div className="flex justify-between">
            <div className="flex gap-2 items-center">
                <div className={`size-5 rounded-full flex justify-center items-center text-white text-xs font-medium ${colors[color].badge}`}>{index}</div>
                <div className="font-medium text-sm">{title}</div>
            </div>
            {removeButton}
        </div>

        {children}
    </div>
}

export function ClearanceWaypointsFormStimulus({initialData, submitUrl}: ClearanceWaypointsFormProps) {
    const {
        handleSubmit,
        control,
    } = useForm<ClearanceWaypointsFormValues>({
        shouldFocusError: true,
        resolver: valibotResolver(ClearanceWaypointsSchema),
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            fromAddress: {elevator: null, floor: null, address: null},
            // toAddress:   {elevator: null, floor: null, address: null},
            // stopoverAddresses: [],
            ...(initialData ?? {})
        },
    })

    const {fields, append, remove} = useFieldArray({
        control,
        name: "stopoverAddresses",
    })

    const {execute, isSubmitting, serverErrors} = useFormSubmission<ClearanceWaypointsFormValues>({submitUrl});
    const Divider = null;

    return (
        <form onSubmit={handleSubmit(execute)} className="space-y-4">
            <AddressContainer {...{title: 'Adresa k vyklizení', index: 1, color: 'blue'}}>
                <AddressSection
                    control={control}
                    namePrefix="fromAddress"
                />
            </AddressContainer>

            <FormServerErrors errors={serverErrors} />

            <div className="text-sm text-slate-500">
                Pokračováním souhlasíte s <a href="/web/static/terms" className="underline">Podmínkami použivání a zásadami ochrany osobních údajů</a>
            </div>

            <ContinueSubmitButton {...{isSubmitting}}/>

        </form>
    );
}